<template lang="pug">
    div
        .table-container
            table
                tr.list
                    td.list-item.list-item__name
                        .list-item.list-item-header(v-if="type === 'lesson-conducting-template'")
                            a.list-item-header__ref(@click.prevent="showChooseGameModal = true")
                                | {{ profile.name }}
                        .list-item.list-item-header(v-else) {{ profile.name }}
                    td.list-item.list-item__courses
                        .list-item.list-item-header {{ $i18n.t('list.list_templatePreview_courses') }}:
                        .list-item__course(v-for="course in profile.courses") {{ course.name }}
                    td.list-item
                        .list-item.list-item-header {{ $i18n.t('list.list_templatePreview_games') }}:
                        .list-item__games
                            div(v-for="game in profile.games" :title='getGameSettingsTitle(game)')
                                | {{ getGameName(game) }}
                    td.list-item.list-item__actions
                        .actions
                            dropdown(:icon='false' without-move)
                                template(slot="link")
                                    svg-icon.homework-template-preview__icon.homework-template-preview__icon_blue(name="select", :size="selectorSize")
                                ul.menu-list
                                    li(:disabled="profile.userId !== $store.state.auth.user.id && !$store.state.auth.user.isSuperadmin")
                                        router-link.action.actions__edit(:to="{name: `${type}:edit`, params: {id: profile.id, type: type}}")
                                            | {{ $i18n.t('list.list_common_actionsEdit') }}
                                    li
                                        router-link.action.actions__copy(:to="{name: `${type}:create`, params: {copy: profile, type: type}}")
                                            | {{ $i18n.t('list.list_homeworkPreview_actionsCopy') }}
                                    li(:disabled="profile.userId !== $store.state.auth.user.id && !$store.state.auth.user.isSuperadmin")
                                        a.action.actions__delete(@click='showConfirmDeleteModal = true')
                                            | {{ $i18n.t('list.list_common_actionsDelete') }}
        divider(:margin="'10px auto'")
        confirm-delete-modal(
            v-if="showConfirmDeleteModal",
            @close="showConfirmDeleteModal = false",
            @delete="$emit('delete', profile.id)"
        ) {{ " " + $i18n.t('list.list_templatePreview_thisTemplate') }}
        cs-modal(v-if="type === 'lesson-conducting-template' && showChooseGameModal", @close="closeChooseGameModal")
            div(slot="header") {{ $i18n.t('list.list_templatePreview_chooseGame') }}
            .trainer-modal-content
                .trainer-modal-content__game-item(
                    v-for="(game, index) in profile.games"
                    @click="chosenGameIndex = index"
                    @dblclick="navigateToGame",
                    :class=`{
                        'trainer-modal-content__game-item_selected': index === chosenGameIndex,
                        'trainer-modal-content__game-item_opened': index === chosenGameIndex,
                    }`
                )
                    div.trainer-modal-content__game-item__title(
                        :class=`{
                            'trainer-modal-content__game-item__title_selected': index === chosenGameIndex,
                            'trainer-modal-content__game-item__title_closed': index === chosenGameIndex
                        }`
                    )
                        .game-label
                            svg-icon.selected-game-icon(v-if="index === chosenGameIndex", name="circle-check", size="big")
                            | {{ game.name }}
                    .game-content(
                        v-if="index === chosenGameIndex",
                        :class="{'game-content_selected': true}"
                    )
                        div(
                            v-if="game.parameters.default",
                            :is="getGamePreview(game.slug)",
                            :game="game"
                            :userId="1"
                            :withIcon="false"
                        )
            .trainer-modal-button
                cs-button(
                    @click="navigateToGame",
                    :disabled="chosenGameIndex === null"
                )
                    | {{ $i18n.t('list.list_templatePreview_startGame') }}
</template>

<script>
    import Dropdown from '../../../elements/Dropdown.vue';
    import TrainerUtilities from '../../../utils/trainer/trainer';
    const ConfirmDeleteModal = () => import('../../../modals/ConfirmDeleteModal.vue');
    import ProgressPreviewMixin from '../../../utils/progressPreviewMixin';
    import { cloneDeep, kebabCase, camelCase } from 'lodash';
    import { mapState } from 'vuex';
    import BonusGamesApi from '@/api/bonusGames/bonusGames';
    import TrainerGames from '@/config/trainer.json';

    const AddSubPreview = () => import('../../games/games-previews/AddSubPreview.vue');
    const FlashCardsPreview = () => import('../../games/games-previews/FlashCardsPreview.vue');
    const MultDivPreview = () => import('../../games/games-previews/MultDivPreview.vue');
    const TabularMultiplicationPreview = () => import('../../games/games-previews/TabularMultiplicationPreview.vue');
    const EquationPreview = () => import('../../games/games-previews/EquationPreview.vue');
    const FactorizationPreview = () => import('../../games/games-previews/FactorizationPreview.vue');
    const AnzanPreview = () => import('../../games/games-previews/AnzanPreview.vue');
    const PuzzlePreview = () => import('../../games/games-previews/PuzzlePreview.vue');
    const CouplesPreview = () => import('../../games/games-previews/CouplesPreview.vue');
    const ChainsPreview = () => import('../../games/games-previews/ChainsPreview.vue');
    const ScannerPreview = () => import('../../games/games-previews/ScannerPreview.vue');
    const ShutterPreview = () => import('../../games/games-previews/ShutterPreview.vue');
    const ShutterReadingEnPreview = () => import('../../games/games-previews/ShutterReadingEnPreview.vue');
    const ShutterRussianLanguagePreview = () => import('../../games/games-previews/ShutterRussianLanguagePreview.vue');
    const WordsFieldPreview = () => import('../../games/games-previews/WordsFieldPreview.vue');
    const WordsFieldReadingEnPreview = () => import('../../games/games-previews/WordsFieldReadingEnPreview.vue');
    const AnagramsPreview = () => import('../../games/games-previews/AnagramsPreview.vue');
    const TextsPreview = () => import('../../games/games-previews/TextsPreview.vue');
    const LettersFieldPreview = () => import('../../games/games-previews/LettersFieldPreview.vue');
    const CatchWordPreview = () => import('../../games/games-previews/CatchWordPreview.vue');
    const CatchWordReadingEnPreview = () => import('../../games/games-previews/CatchWordReadingEnPreview.vue');
    const VisionFieldPreview = () => import('../../games/games-previews/VisionFieldPreview.vue');
    const VisionFieldReadingEnPreview = () => import('../../games/games-previews/VisionFieldReadingEnPreview.vue');
    const SchultePreview = () => import('../../games/games-previews/SchultePreview.vue');
    const MatrixPreview = () => import('../../games/games-previews/MatrixPreview.vue');
    const NavigatorPreview = () => import('../../games/games-previews/NavigatorPreview.vue');
    const SynonymsPreview = () => import('../../games/games-previews/SynonymsPreview.vue');
    const SynonymsReadingEnPreview = () => import('../../games/games-previews/SynonymsReadingEnPreview.vue');
    const SynonymsRussianLanguagePreview = () => import('../../games/games-previews/SynonymsRussianLanguagePreview.vue');
    const AntonymsPreview = () => import('../../games/games-previews/AntonymsPreview.vue');
    const AntonymsRussianLanguagePreview = () => import('../../games/games-previews/AntonymsRussianLanguagePreview.vue');
    const ClassificationsPreview = () => import('../../games/games-previews/Classifications.vue');
    const SoundCouplesPreview = () => import('../../games/games-previews/SoundCouples.vue');
    const FourthOnePreview = () => import('../../games/games-previews/FourthOne.vue');
    const SearchPreview = () => import('../../games/games-previews/Search.vue');
    const EquationOnlinePreview = () => import('../../games/games-previews/EquationOnlinePreview.vue');
    const DigitCouplesPreview = () => import('../../games/games-previews/DigitCouplesPreview.vue');
    const MemoryPreview = () => import('../../games/games-previews/MemoryPreview.vue');
    const ExpressionsPreview = () => import('../../games/games-previews/ExpressionsPreview.vue');
    const RootsAndDegreesPreview = () => import('../../games/games-previews/RootsAndDegreesPreview');
    const MemorizePreview = () => import('../../games/games-previews/MemorizePreview.vue');
    const KeenEyePreview = () => import('../../games/games-previews/KeenEyePreview.vue');
    const NumericalNetworkPreview = () => import('../../games/games-previews/NumericalNetworkPreview.vue');
    const SequenceOfNumbersPreview = () => import('../../games/games-previews/SequenceOfNumbersPreview.vue');
    const DayOfWeekPreview = () => import('../../games/games-previews/DayOfWeekPreview.vue');
    const RootsAndSquaresPreview = () => import('../../games/games-previews/RootsAndSquaresPreview');
    const LonerPreview = () => import('../../games/games-previews/LonerPreview');
    const LonerBonusGamesPreview = () => import('../../games/games-previews/LonerBonusGamesPreview');
    const AssembleTheNumberPreview = () => import('../../games/games-previews/AssembleTheNumberPreview.vue');
    const AssociationsPreview = () => import('../../games/games-previews/AssociationsPreview.vue');
    const WhoIsWhoPreview = () => import('../../games/games-previews/WhoIsWhoPreview.vue');
    const VocabularyWordsPreview = () => import('../../games/games-previews/VocabularyWordsPreview.vue');
    const SecretsOfCiceroPreview = () => import('../../games/games-previews/SecretsOfCiceroPreview.vue');
    const VocabularyMemoPreview = () => import('../../games/games-previews/VocabularyMemoPreview.vue');
    const CosmoTourPreview = () => import('../../games/games-previews/CosmoTourPreview.vue');
    const ChameleonPreview = () => import('../../games/games-previews/ChameleonPreview.vue');
    const RepeatAfterMePreview = () => import('../../games/games-previews/RepeatAfterMePreview.vue');
    const MentalDrawingPreview = () => import('../../games/games-previews/MentalDrawingPreview.vue');
    const HandsUpPreview = () => import('../../games/games-previews/HandsUpPreview.vue');
    const CorrectiveSamplesPreview = () => import('../../games/games-previews/CorrectiveSamplesPreview.vue');
    const MemoPreview = () => import('../../games/games-previews/MemoPreview.vue');
    const MemoryXPreview = () => import('../../games/games-previews/MemoryXPreview.vue');
    const ChainsBonusGamePreview = () => import('../../games/games-previews/ChainsBonusGamePreview.vue');
    const ScoutPreview = () => import('../../games/games-previews/ScoutPreview.vue');
    const EquationMultiplicationPreview = () => import('../../games/games-previews/EquationMultiplicationPreview.vue');
    const DiadaPreview = () => import('../../games/games-previews/DiadaPreview.vue');
    const AlpariPreview = () => import('../../games/games-previews/AlpariPreview.vue');
    const ChainsReadingEnPreview = () => import('../../games/games-previews/ChainsReadingEnPreview.vue');
    const CouplesReadingEnPreview = () => import('../../games/games-previews/CouplesReadingEnPreview.vue');
    const DivideItRightPreview = () => import('../../games/games-previews/DivideItRightPreview.vue');
    const ChooseItRightPreview = () => import('../../games/games-previews/ChooseItRightPreview.vue');
    const DivideUnstressedVowelsPreview = () => import('../../games/games-previews/DivideUnstressedVowelsPreview.vue');
    const WordStructurePreview = () => import('../../games/games-previews/WordStructurePreview.vue');
    const SentenceParsingPreview = () => import('../../games/games-previews/SentenceParsingPreview.vue');
    const VocabularyWordsRusPreview = () => import('../../games/games-previews/VocabularyWordsRusPreview.vue');
    const LettersSoundsSyllablesPreview = () => import('../../games/games-previews/LettersSoundsSyllablesPreview.vue');
    const TransferSyllablesPreview = () => import('../../games/games-previews/TransferSyllablesPreview.vue');
    const TransferSyllablesSchoolPreparingPreview = () => import('../../games/games-previews/TransferSyllablesSchoolPreparingPreview.vue');
    const HardSoftConsonantsPreview = () => import('../../games/games-previews/HardSoftConsonantsPreview.vue');
    const HardSoftConsonantsSchoolPreparingPreview = () => import('../../games/games-previews/HardSoftConsonantsSchoolPreparingPreview.vue');
    const VocabularyMemoRusPreview = () => import('../../games/games-previews/VocabularyMemoRusPreview.vue');
    const WordDrumPreview = () => import('../../games/games-previews/WordDrumPreview.vue');
    const TranscriptionInWordPreview = () => import('../../games/games-previews/TranscriptionInWordPreview.vue');
    const OrthographPreview = () => import('../../games/games-previews/OrthographPreview.vue');
    const VoicedAndDeafConsonantsPreview = () =>
        import('../../games/games-previews/VoicedAndDeafConsonantsPreview.vue');
    const PartsOfSpeechPreview = () => import('../../games/games-previews/PartsOfSpeechPreview.vue');
    const ParsingSentencePartsSpeechPreview = () => import('../../games/games-previews/ParsingSentencePartsSpeechPreview.vue');
    const GenericWordsPreview = () => import('../../games/games-previews/GenericWordsPreview.vue');
    const VerbTensesPreview = () => import('../../games/games-previews/VerbTensesPreview.vue');
    const SortOneManyPreview = () => import('../../games/games-previews/SortOneManyPreview.vue');
    const PunctuationMarksPreview = () => import('../../games/games-previews/PunctuationMarksPreview.vue');
    const AlphabetPreview = () => import('../../games/games-previews/AlphabetPreview.vue');
    const ConnectThePuzzlesPreview = () => import('../../games/games-previews/ConnectThePuzzlesPreview.vue');

    export default {
        mixins: [TrainerUtilities, ProgressPreviewMixin],
        props: {
            profile: Object,
            type: {
                type: String,
                required: true,
            },
            page: {
                type: Number,
                default: 1,
            },
        },
        data() {
            return {
                showConfirmDeleteModal: false,
                showChooseGameModal: false,
                chosenGameIndex: null,
            };
        },
        mounted() {
            if (this.lessonConductingTemplate?.id === this.profile.id) {
                this.showChooseGameModal = true;
                this.chosenGameIndex = this.lessonConductingTemplate.chosenGameIndex;
            }
        },
        computed: {
            ...mapState({
                lessonConductingTemplate: state => state.trainer.lessonConductingTemplate,
                user: state => state.auth.user,
            }),
            selectorSize() {
                const windowWidth = window.innerWidth;
                return windowWidth >= 1200 ? 'xx-large' : 'x-large';
            },
            bonusGames() {
                if (!this.courseType) return [];
                const courseTypeKebab = kebabCase(this.courseType);
                return TrainerGames.games.filter(
                    ({ courseType, isBonusGame }) => courseType === courseTypeKebab && isBonusGame
                );
            },
            courseType() {
                return this.lessonConductingTemplate.coursesType;
            },
        },
        watch: {
            chosenGameIndex() {
                this.$store.dispatch('setLessonConductingTemplate', {
                    ...this.profile,
                    chosenGameIndex: this.chosenGameIndex,
                    page: this.page,
                });
            },
        },
        methods: {
            closeChooseGameModal() {
                this.showChooseGameModal = false;
            },
            getGamePreview(game) {
                switch (game) {
                    case 'MultiplicationAndDivision':
                    case 'MultiplicationAndDivisionMathMagic':
                        return 'mult-div-preview';
                    case 'TabularMultiplication':
                        return 'tabular-multiplication-preview';
                    case 'InteractiveFlashCards':
                    case 'FlashCards':
                        return 'flash-cards-preview';
                    case 'AdditionAndSubtraction':
                    case 'AdditionAndSubtractionMathMagic':
                    case 'AdditionAndSubtractionWithTime':
                        return 'add-sub-preview';
                    case 'ShutterOnline':
                        return 'shutter-preview';
                    case 'WordsFieldOnline':
                        return 'words-field-preview';
                    case 'WordsFieldReadingEn':
                    case 'WordsFieldReadingEnWithTime':
                        return 'words-field-reading-en-preview';
                    case 'AnagramsOnline':
                        return 'anagrams-preview';
                    case 'TextsOnline':
                        return 'texts-preview';
                    case 'LettersFieldOnline':
                        return 'letters-field-preview';
                    case 'CatchWordOnline':
                        return 'catch-word-preview';
                    case 'SynonymsOnline':
                        return 'synonyms-preview';
                    case 'NumericalNetwork':
                        return 'numerical-network-preview';
                    case 'KeenEye':
                    case 'KeenEyeBonusGames':
                        return 'keen-eye-preview';
                    case 'Memorize':
                        return 'memorize-preview';
                    case 'SequenceOfNumbers':
                        return 'sequence-of-numbers-preview';
                    case 'DayOfWeek':
                        return 'day-of-week-preview';
                    case 'AssembleTheNumber':
                        return 'assemble-the-number-preview';
                    case 'RootsAndSquares':
                        return 'roots-and-squares-preview';
                    case 'Loner':
                        return 'loner-preview';
                    case 'SecretsOfCiceroPreview':
                        return 'secrets-of-cicero-preview';
                    case 'VocabularyMemoPreview':
                        return 'vocabulary-memo-preview';
                    case 'ChameleonPreview':
                        return 'chameleon-preview';
                    case 'MentalDrawingPreview':
                        return 'mental-drawing-preview';
                    case 'HandsUpPreview':
                        return 'hands-up-preview';
                    case 'RepeatAfterMePreview':
                        return 'repeat-after-me-preview';
                    case 'CorrectiveSamplesPreview':
                        return 'corrective-samples-preview';
                    case 'MemoBonusGames':
                        return 'memo-preview';
                    case 'NavigatorBonusGames':
                        return 'navigator-preview';
                    case 'ChainsReadingEn':
                        return 'chains-reading-en-preview';
                    case 'CouplesReadingEn':
                        return 'couples-reading-en-preview';
                    case 'CatchWordReadingEn':
                        return 'catch-word-reading-en-preview';
                    case 'VisionFieldReadingEn':
                        return 'vision-field-reading-en-preview';
                    case 'ShutterReadingEn':
                        return 'shutter-reading-en-preview';
                    case 'SynonymsReadingEn':
                        return 'synonyms-reading-en-preview';
                    case 'AntonymsRussianLanguage':
                        return 'antonyms-russian-language-preview';
                    default:
                        return `${kebabCase(game)}-preview`;
                }
            },
            async isBonusGameHasAccess(game) {
                const params = {
                    courseType: kebabCase(this.courseType),
                    gameName: game.slug,
                    schoolsIds: this.user.schoolsIds,
                };
                const data = await BonusGamesApi.checkAccessForBonusGame(params);
                return Boolean(data.access);
            },
            routeToGame(game) {
                this.$store.dispatch('setGame', cloneDeep(game.parameters.default));
                this.$router.push({
                    name: `${kebabCase(game.slug)}:start`,
                });
            },
            async navigateToGame() {
                this.showChooseGameModal = false;
                const activeGame = this.profile.games[this.chosenGameIndex];
                activeGame.parameters.default.fromLessonConductingTemplate = true;
                activeGame.parameters.default.slug = activeGame.slug;
                activeGame.parameters.default.courseType = this.courseType;

                const bonusGame = this.bonusGames.find(({ slug }) => slug === activeGame.slug);
                if (!bonusGame) return this.routeToGame(activeGame);

                const isBonusGameHasAccess = await this.isBonusGameHasAccess(activeGame);
                if (isBonusGameHasAccess) {
                    return this.routeToGame(activeGame);
                }
                this.$toasted.error(
                    this.$i18n.t('homework.homework_create_bonusPlayError', {
                        bonusGameName: this.$i18n.t('common.common_gameName_' + camelCase(activeGame.slug)),
                    })
                );
            },
        },
        components: {
            Dropdown,
            ConfirmDeleteModal,

            EquationMultiplicationPreview,
            AddSubPreview,
            FlashCardsPreview,
            MultDivPreview,
            TabularMultiplicationPreview,
            EquationPreview,
            FactorizationPreview,
            AnzanPreview,
            PuzzlePreview,
            CouplesPreview,
            ChainsPreview,
            ScannerPreview,
            ExpressionsPreview,
            ShutterPreview,
            ShutterReadingEnPreview,
            ShutterRussianLanguagePreview,
            WordsFieldPreview,
            WordsFieldReadingEnPreview,
            AnagramsPreview,
            TextsPreview,
            LettersFieldPreview,
            CatchWordPreview,
            CatchWordReadingEnPreview,
            VisionFieldPreview,
            VisionFieldReadingEnPreview,
            SchultePreview,
            MatrixPreview,
            NavigatorPreview,
            SynonymsPreview,
            SynonymsReadingEnPreview,
            SynonymsRussianLanguagePreview,
            AntonymsPreview,
            AntonymsRussianLanguagePreview,
            ClassificationsPreview,
            SoundCouplesPreview,
            FourthOnePreview,
            SearchPreview,
            EquationOnlinePreview,
            DigitCouplesPreview,
            MemoryPreview,
            RootsAndDegreesPreview,
            MemorizePreview,
            KeenEyePreview,
            NumericalNetworkPreview,
            SequenceOfNumbersPreview,
            DayOfWeekPreview,
            RootsAndSquaresPreview,
            LonerPreview,
            LonerBonusGamesPreview,
            AssembleTheNumberPreview,
            AssociationsPreview,
            WhoIsWhoPreview,
            SecretsOfCiceroPreview,
            VocabularyWordsPreview,
            VocabularyMemoPreview,
            CosmoTourPreview,
            ChameleonPreview,
            HandsUpPreview,
            MentalDrawingPreview,
            RepeatAfterMePreview,
            CorrectiveSamplesPreview,
            MemoPreview,
            MemoryXPreview,
            ChainsBonusGamePreview,
            ScoutPreview,
            DiadaPreview,
            AlpariPreview,
            ChainsReadingEnPreview,
            CouplesReadingEnPreview,
            TransferSyllablesPreview,
            TransferSyllablesSchoolPreparingPreview,
            HardSoftConsonantsPreview,
            HardSoftConsonantsSchoolPreparingPreview,
            VocabularyMemoRusPreview,
            WordDrumPreview,
            OrthographPreview,
            TranscriptionInWordPreview,
            ConnectThePuzzlesPreview,
            AlphabetPreview,
            DivideItRightPreview,
            ChooseItRightPreview,
            DivideUnstressedVowelsPreview,
            WordStructurePreview,
            SentenceParsingPreview,
            VocabularyWordsRusPreview,
            LettersSoundsSyllablesPreview,
            VoicedAndDeafConsonantsPreview,
            PartsOfSpeechPreview,
            ParsingSentencePartsSpeechPreview,
            VerbTensesPreview,
            GenericWordsPreview,
            SortOneManyPreview,
            PunctuationMarksPreview,
        },
    };
</script>

<style lang="scss" scoped>
    .table-container {
        overflow: auto;
        table {
            width: 100%;
            min-height: 210px;
            @media screen and (max-width: 767px) {
                min-width: 768px;
            }
        }
    }
    .list {
        justify-content: space-between;
        margin: 20px 30px;
        font-weight: normal;
    }
    .list-item {
        vertical-align: top;
    }
    .list-item-header {
        font-weight: 500;
        font-size: 16px;
        line-height: 24px;
        color: #333333;
    }
    .list-item-header__ref {
        color: $hsl-color-blue-56_5;
        text-decoration: underline;
    }
    .list-item-header__ref:hover {
        cursor: pointer;
    }
    .list-item__name {
        width: 20%;
    }
    .list-item__games {
        width: 100%;
        text-align: left;
        font-style: normal;
        font-weight: normal;
        font-size: 16px;
        line-height: 24px;
        color: #333333;
    }
    .list-item__courses {
        width: 30%;
    }
    .list-item__actions {
        width: 5%;
    }
    .actions {
        display: flex;
        align-items: center;
        text-align: left;
        .menu-list {
            li {
                margin: 7px 0;
            }
        }
        .action {
            cursor: pointer;
            font-size: 16px;
            line-height: 26px;
            color: $hsl-color-blue-56;
            text-decoration: underline;
            &:hover {
                color: $hsl-color-blue-56;
                text-decoration: none;
            }
        }
    }
    .actions__link {
        display: flex;
        align-items: center;
    }
    .homework-template-preview__icon_blue {
        color: $hsl-color-blue-56;
    }

    .trainer-modal-content,
    .trainer-modal-button {
        margin-top: 20px;
    }

    .trainer-modal-content {
        max-height: 50vh;
        overflow-y: auto;
    }
    .trainer-modal-content__game-item {
        border-radius: 10px;
        cursor: pointer;
        font-weight: normal;
        font-size: 16px;
        line-height: 24px;
        color: #333333;
        box-sizing: border-box;
        max-width: 475px;
        margin: 0 auto 20px auto;
        &_opened {
            border: 1px solid #f2f2ff;
        }
        &_selected {
            background: #f2fff2;
            border: 1px solid #6bbd6c;
        }
    }
    .trainer-modal-content__game-item__title {
        padding: 20px;
        border-radius: 10px;
        border: 1px solid #f2f2ff;
        background: #f2f2ff;
        font-size: 16px;
        display: flex;
        justify-content: space-between;
        &_selected {
            background: #f2fff2;
            border-bottom: 1px solid #6bbd6c;
        }
        &_closed {
            border-bottom: none;
        }
    }
    .selected-game-icon {
        color: #6bbd6c;
        margin-right: 10px;
    }
    .game-label {
        font-weight: 500;
        font-size: 20px;
        line-height: 24px;
        color: #333333;
        display: flex;
        align-items: center;
    }

    .game-content {
        background: #ffffff;
        border: 1px solid #f2f2ff;
        border-top: none;
        box-sizing: border-box;
        border-radius: 0 0 10px 10px;
        margin-bottom: 20px;
        &_selected {
            background: #f2fff2;
        }
    }
</style>
